// The small status chips the Academy puts on cards and lesson headers:
// "New" (blue, same as Investigations' NewChip), "Done" (green), "Walkthrough"
// / "Watch" (neutral), the platform hint, and the gate lock. One component,
// one size, so every list reads the same.
import { CheckIcon, LockClosedIcon, PlayCircleIcon, SparklesIcon, CursorArrowRaysIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Tooltip from "../../components/common/Tooltip";

export type ChipTone = "blue" | "green" | "neutral" | "amber";

const TONE: Record<ChipTone, string> = {
  blue: "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-900/60",
  green: "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 ring-1 ring-green-200 dark:ring-green-900/60",
  amber: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 ring-1 ring-amber-200 dark:ring-amber-900/60",
  neutral: "bg-base-200 text-base-content/60 ring-1 ring-base-300",
};

export const Chip: React.FC<{ tone?: ChipTone; icon?: React.ReactNode; children: React.ReactNode; title?: string; className?: string }> = ({
  tone = "neutral",
  icon,
  children,
  title,
  className,
}) => (
  <span
    title={title}
    className={cn("inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase tracking-wider px-1.5 py-px leading-4 whitespace-nowrap", TONE[tone], className)}
  >
    {icon}
    {children}
  </span>
);

export const NewChip = () => (
  <Chip tone="blue" icon={<SparklesIcon className="size-3" />}>
    New
  </Chip>
);
export const DoneChip = () => (
  <Chip tone="green" icon={<CheckIcon className="size-3" />}>
    Done
  </Chip>
);
export const WalkthroughChip = () => (
  <Chip icon={<CursorArrowRaysIcon className="size-3" />} title="Can be run live in your account">
    Walkthrough
  </Chip>
);
export const WatchChip = () => (
  <Chip icon={<PlayCircleIcon className="size-3" />} title="Has a narrated player">
    Watch
  </Chip>
);
export const PlatformChip: React.FC<{ platform: "web" | "ios" }> = ({ platform }) => <Chip>{platform === "ios" ? "iOS" : "Web"}</Chip>;

export const GateChip: React.FC<{ label: string; locked: boolean; reason: string }> = ({ label, locked, reason }) => (
  <Tooltip overlay={reason} placement="top">
    <span className="inline-flex">
      <Chip tone={locked ? "amber" : "neutral"} icon={locked ? <LockClosedIcon className="size-3" /> : undefined}>
        {label}
      </Chip>
    </span>
  </Tooltip>
);

export const MinutesChip: React.FC<{ minutes: number }> = ({ minutes }) => <span className="text-xs text-base-content/60 whitespace-nowrap">{minutes} min</span>;
